import type { PDFInstance } from "types/viewer.type"
import type {
	TAnnotationCategory,
	TAnnotationFillState,
	TAnnotationMetadata,
	TAnnotationStatus,
	TAnnotationStyleConfig,
} from "types/annotations.type"
import type {
	TEcall,
	TErefStatus,
	TFurniture,
	TFurnitureStatus,
	TPageElevationData,
} from "types/elevationReferences.type"
import type { Core } from "@pdftron/webviewer"

type TPageRotationTransform = {
	rotation: number
	origWidth: number
	origHeight: number
}

const ANNOTATION_STYLES: TAnnotationStyleConfig = {
	connected: {
		fillColor: [255, 234, 0, 0.3],
	},
	unconnected: {
		fillColor: [189, 189, 189, 0.3],
	},
	reviewed: {
		fillColor: [0, 71, 255, 0.3],
	},
	in_quotation: {
		fillColor: [112, 199, 105, 0.4],
	},
}

const resolveEntityFillState = (
	entityStatus: TErefStatus | TFurnitureStatus | undefined,
	connectivity: TAnnotationStatus,
): TAnnotationFillState => {
	if (entityStatus === "in_quotation") return "in_quotation"
	if (entityStatus === "reviewed") return "reviewed"
	return connectivity
}

class AnnotationService {
	private instance: PDFInstance | null = null
	private annotationsByCategory: Map<TAnnotationCategory, Set<Core.Annotations.Annotation>> = new Map()
	private metadataMap: Map<string, TAnnotationMetadata> = new Map()
	// Incremented on every centerAnnotation call so a newer scroll cancels the
	// async/deferred work (zoom settle + verify loop) of any earlier in-flight one.
	private scrollGeneration = 0
	onAnnotationSelected?: (metadata: TAnnotationMetadata) => void
	// Stored so setupClickListener can be idempotent: the single shared Quotations instance
	// re-registers on every preview↔editor switch (#1119), so we must remove the prior
	// listener before adding a new one (and on cleanup) to avoid stacking duplicates.
	private annotationSelectedHandler: ((annotations: Core.Annotations.Annotation[], action: string) => void) | null =
		null

	setInstance(instance: PDFInstance): void {
		this.instance = instance
		instance.Core.annotationManager.promoteUserToAdmin()
		this.setupClickListener()
	}

	renderAnnotations(pages: TPageElevationData[]): void {
		if (!this.instance) return

		this.clearAllAnnotations()

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const allAnnotations: Core.Annotations.Annotation[] = []

		// Per-page rotation lookup (intrinsic /Rotate degrees) + unrotated page dims for
		// the coordinate transform. Backend coords are in original (unrotated) page space;
		// we rotate them so they line up with what WebViewer renders.
		const pageTransforms = this.buildPageRotationMap(doc, pages)

		// Build set of eref IDs that have furniture
		const erefsWithFurniture = new Set<number>()
		for (const page of pages) {
			for (const f of page.furniture) {
				if (f.eref_id) erefsWithFurniture.add(f.eref_id)
			}
		}

		// Pass 1 — erefs (drawn at bottom). Seed each eref's connectivity from the furniture
		// set; this is the single place where furniture presence maps to eref connectivity.
		for (const page of pages) {
			// API pages are 0-based, WebViewer is 1-based
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const eref of page.erefs) {
				const status: TAnnotationStatus = erefsWithFurniture.has(eref.id) ? "connected" : "unconnected"
				const category: TAnnotationCategory = `eref:${status}`
				const fillState = resolveEntityFillState(eref.status, status)
				const coords = this.applyRotation(eref.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				this.applyStyle(annot, fillState, Annotations)
				this.setReadOnly(annot)
				annot.setContents(this.buildErefLabel(eref.elevation_number))

				annot.setCustomData("entityId", String(eref.id))
				annot.setCustomData("entityType", "eref")
				annot.setCustomData("status", status)
				annot.setCustomData("entityStatus", eref.status ?? "")
				annot.setCustomData("elevationNumber", eref.elevation_number ?? "")
				annot.setCustomData("pageName", eref.page_name)
				annot.setCustomData("pageNumber", String(eref.page_number))

				const metadata: TAnnotationMetadata = {
					entityId: eref.id,
					entityType: "eref",
					status,
					pageNumber: eref.page_number,
					elevationNumber: eref.elevation_number ?? undefined,
					pageName: eref.page_name,
					renderedRect: eref.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		// Pass 2 — ecalls. Every eref annotation now exists, so connectivity is read through
		// isEcallConnected → isErefConnected — the same predicate the runtime cascade uses,
		// so the render and live paths can't drift apart.
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const ecall of page.ecalls) {
				const linkedErefIds = this.extractLinkedErefIds(ecall)
				const status: TAnnotationStatus = this.isEcallConnected(linkedErefIds) ? "connected" : "unconnected"
				const category: TAnnotationCategory = `ecall:${status}`
				const coords = this.applyRotation(ecall.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				// Ecalls don't carry review/in_quotation status — connectivity drives color.
				this.applyStyle(annot, status, Annotations)
				this.setReadOnly(annot)
				annot.setContents(this.buildEcallLabel(ecall))

				annot.setCustomData("entityId", String(ecall.id))
				annot.setCustomData("entityType", "ecall")
				annot.setCustomData("status", status)
				annot.setCustomData("roomName", ecall.room_name ?? "")
				annot.setCustomData("pageName", ecall.page_name ?? "")
				annot.setCustomData("pageNumber", String(ecall.page_number))
				annot.setCustomData("linkedErefIds", JSON.stringify(linkedErefIds))

				const metadata: TAnnotationMetadata = {
					entityId: ecall.id,
					entityType: "ecall",
					status,
					pageNumber: ecall.page_number,
					roomName: ecall.room_name ?? undefined,
					pageName: ecall.page_name ?? undefined,
					renderedRect: ecall.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		// Pass 3 — furniture (drawn on top).
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const furniture of page.furniture) {
				const status: TAnnotationStatus = "connected"
				const category: TAnnotationCategory = `furniture:${status}`
				const fillState = resolveEntityFillState(furniture.status, status)
				const coords = this.applyRotation(furniture.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				this.applyStyle(annot, fillState, Annotations)
				this.setReadOnly(annot)
				annot.setContents(this.buildFurnitureLabel(furniture))

				annot.setCustomData("entityId", String(furniture.id))
				annot.setCustomData("entityType", "furniture")
				annot.setCustomData("status", status)
				annot.setCustomData("entityStatus", furniture.status ?? "")
				annot.setCustomData("erefId", String(furniture.eref_id))
				annot.setCustomData("category", furniture.category?.name ?? "")
				annot.setCustomData("pageNumber", String(furniture.page_number))

				const metadata: TAnnotationMetadata = {
					entityId: furniture.id,
					entityType: "furniture",
					status,
					pageNumber: furniture.page_number,
					erefId: furniture.eref_id ?? undefined,
					renderedRect: furniture.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		annotationManager.addAnnotations(allAnnotations)
		annotationManager.drawAnnotationsFromList(allAnnotations)
	}

	clearAllAnnotations(): void {
		if (this.instance) {
			const { documentViewer, annotationManager } = this.instance.Core
			const doc = documentViewer.getDocument()

			if (doc && this.annotationsByCategory.size > 0) {
				const allAnnotations: Core.Annotations.Annotation[] = []
				for (const annotations of this.annotationsByCategory.values()) {
					for (const annot of annotations) {
						allAnnotations.push(annot)
					}
				}
				annotationManager.deleteAnnotations(allAnnotations)
			}
		}

		this.annotationsByCategory.clear()
		this.metadataMap.clear()
	}

	setCategoryVisibility(category: TAnnotationCategory, visible: boolean): void {
		if (!this.instance) return

		const annotations = this.annotationsByCategory.get(category)
		if (!annotations) return

		const { annotationManager } = this.instance.Core
		for (const annot of annotations) {
			if (visible) {
				annotationManager.showAnnotation(annot)
			} else {
				annotationManager.hideAnnotation(annot)
			}
		}
	}

	getMetadata(annotationId: string): TAnnotationMetadata | undefined {
		return this.metadataMap.get(annotationId)
	}

	async scrollToEref(erefId: number): Promise<void> {
		if (!this.instance) return
		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return
		await this.centerAnnotation(annot, "bottomLeft")
	}

	async scrollToFurniture(furnitureId: number): Promise<void> {
		if (!this.instance) return
		const annot = this.findEntityAnnotation("furniture", furnitureId)
		if (!annot) return
		await this.centerAnnotation(annot)
	}

	async scrollToEcall(ecallId: number): Promise<void> {
		if (!this.instance) return
		const annot = this.findEntityAnnotation("ecall", ecallId)
		if (!annot) return
		await this.centerAnnotation(annot)
	}

	selectEref(erefId: number): void {
		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	selectFurniture(furnitureId: number): void {
		const annot = this.findEntityAnnotation("furniture", furnitureId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	selectEcall(ecallId: number): void {
		const annot = this.findEntityAnnotation("ecall", ecallId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	// Select the entity annotation under a mouse event ourselves, bypassing Apryse's
	// built-in click-to-select. Once a second WebViewer instance has existed in the page
	// (the Documents-style preview viewer on the Quotations tab), Apryse's edit tool silently
	// stops firing `annotationSelected` on click — but hit-testing and programmatic selection
	// both still work. So we hit-test the click and select manually; the existing
	// `annotationSelected` listeners then open the reference sidebar as usual. See #1119.
	// Returns true if one of our entity annotations was hit (and selected).
	selectAnnotationFromMouseEvent(event: MouseEvent): boolean {
		if (!this.instance) return false
		const { annotationManager } = this.instance.Core
		const annot = annotationManager.getAnnotationByMouseEvent(event)
		// Only act on our own entity highlights (furniture/eref/ecall live in metadataMap);
		// ignore clicks on empty space, widgets, or links so the default tool keeps handling them.
		if (!annot || !this.metadataMap.has(annot.Id)) return false
		this.selectAnnotation(annot)
		return true
	}

	// Ids of the currently-selected furniture highlights (skips erefs/ecalls and any non-entity
	// annotation). Copy is furniture-only and single-item, so the caller uses this to reject a
	// multi-selection and to copy exactly the selected furniture.
	getSelectedFurnitureIds(): number[] {
		if (!this.instance) return []
		const { annotationManager } = this.instance.Core
		const ids: number[] = []
		for (const annot of annotationManager.getSelectedAnnotations()) {
			const metadata = this.metadataMap.get(annot.Id)
			if (metadata?.entityType === "furniture") ids.push(metadata.entityId)
		}
		return ids
	}

	private selectAnnotation(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return
		const { annotationManager } = this.instance.Core
		const selected = annotationManager.getSelectedAnnotations()
		if (selected.length === 1 && selected[0].Id === annot.Id) return
		annotationManager.deselectAllAnnotations()
		annotationManager.selectAnnotation(annot)
	}

	updateErefAnnotationStatus(erefId: number, status: TAnnotationStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const entityStatus = (annot.getCustomData("entityStatus") || undefined) as TErefStatus | undefined
		const fillState = resolveEntityFillState(entityStatus, status)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("status", status)

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, { ...existingMetadata, status })
		}

		const oppositeStatus: TAnnotationStatus = status === "connected" ? "unconnected" : "connected"
		const oldCategory: TAnnotationCategory = `eref:${oppositeStatus}`
		const newCategory: TAnnotationCategory = `eref:${status}`
		this.annotationsByCategory.get(oldCategory)?.delete(annot)
		if (!this.annotationsByCategory.has(newCategory)) {
			this.annotationsByCategory.set(newCategory, new Set())
		}
		this.annotationsByCategory.get(newCategory)!.add(annot)

		annotationManager.redrawAnnotation(annot)

		// An eref's connectivity just flipped — cascade to any ecalls that link to it so
		// the ecall turns yellow once one of its erefs gains furniture, and back to gray
		// once all of its erefs lose theirs.
		this.refreshEcallsForEref(erefId)
	}

	// Recompute the connectivity color of every ecall that links to the given eref.
	private refreshEcallsForEref(erefId: number): void {
		const categories: TAnnotationCategory[] = ["ecall:connected", "ecall:unconnected"]
		for (const category of categories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			// Copy the set — recomputeEcallConnectivity may move annots between categories.
			for (const annot of [...set]) {
				if (this.getLinkedErefIds(annot).includes(erefId)) {
					this.recomputeEcallConnectivity(annot)
				}
			}
		}
	}

	private getLinkedErefIds(annot: Core.Annotations.Annotation): number[] {
		const raw = annot.getCustomData("linkedErefIds")
		if (!raw) return []
		try {
			const parsed = JSON.parse(raw) as unknown
			return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : []
		} catch {
			return []
		}
	}

	private extractLinkedErefIds(ecall: TEcall): number[] {
		return ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id !== null)
	}

	// Single source of truth for eref connectivity: an eref is "connected" (has furniture)
	// iff its annotation says so. The status is seeded from the furniture set during
	// renderAnnotations and kept current at runtime by updateErefAnnotationStatus.
	private isErefConnected(erefId: number): boolean {
		return this.findEntityAnnotation("eref", erefId)?.getCustomData("status") === "connected"
	}

	// An ecall is "connected" (yellow) iff at least one of its linked erefs is connected.
	private isEcallConnected(linkedErefIds: number[]): boolean {
		return linkedErefIds.some((id) => this.isErefConnected(id))
	}

	// Re-derive an ecall's connectivity from its linked erefs' current statuses: yellow
	// when at least one linked eref is connected, gray otherwise. No-op when unchanged.
	private recomputeEcallConnectivity(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return

		const { Annotations, annotationManager } = this.instance.Core
		const currentStatus = (annot.getCustomData("status") || "unconnected") as TAnnotationStatus
		const status: TAnnotationStatus = this.isEcallConnected(this.getLinkedErefIds(annot))
			? "connected"
			: "unconnected"
		if (status === currentStatus) return

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, status, Annotations)
		annot.setCustomData("status", status)

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, { ...existingMetadata, status })
		}

		const oldCategory: TAnnotationCategory = `ecall:${currentStatus}`
		const newCategory: TAnnotationCategory = `ecall:${status}`
		this.annotationsByCategory.get(oldCategory)?.delete(annot)
		if (!this.annotationsByCategory.has(newCategory)) {
			this.annotationsByCategory.set(newCategory, new Set())
		}
		this.annotationsByCategory.get(newCategory)!.add(annot)

		annotationManager.redrawAnnotation(annot)
	}

	// Resync an ecall annotation's linked erefs from the source data and recompute its
	// connectivity color. Call after any flow that adds or removes an ecall↔eref link
	// (auto-connect / detach / remove), since the annotation layer is only fully
	// re-rendered on document load — cache changes alone don't repaint it.
	updateEcallLinks(ecallId: number, erefIds: number[]): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("ecall", ecallId)
		if (!annot) return

		annot.setCustomData("linkedErefIds", JSON.stringify(erefIds))
		this.recomputeEcallConnectivity(annot)
	}

	// Convenience over updateEcallLinks: derives the linked eref ids straight from an
	// ecall's elevation_numbers and resyncs its highlight. Use this when a flow already
	// holds the updated ecall (e.g. after writing it to the cache).
	syncEcallConnectivity(ecall: TEcall): void {
		this.updateEcallLinks(ecall.id, this.extractLinkedErefIds(ecall))
	}

	updateErefEntityStatus(erefId: number, entityStatus: TErefStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const connectivity = (annot.getCustomData("status") || "unconnected") as TAnnotationStatus
		const fillState = resolveEntityFillState(entityStatus, connectivity)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("entityStatus", entityStatus)
		annotationManager.redrawAnnotation(annot)
	}

	updateFurnitureEntityStatus(furnitureId: number, entityStatus: TFurnitureStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("furniture", furnitureId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const connectivity = (annot.getCustomData("status") || "connected") as TAnnotationStatus
		const fillState = resolveEntityFillState(entityStatus, connectivity)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("entityStatus", entityStatus)
		annotationManager.redrawAnnotation(annot)
	}

	updateFurnitureAnnotation(furniture: TFurniture): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("furniture", furniture.id)
		if (!annot) return

		const { annotationManager } = this.instance.Core
		const newLabel = this.buildFurnitureLabel(furniture)

		annot.setCustomData("category", furniture.category?.name ?? "")
		annot.setCustomData("erefId", String(furniture.eref_id))

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, {
				...existingMetadata,
				erefId: furniture.eref_id ?? undefined,
			})
		}

		try {
			// Apryse-recommended way to update annotation contents — also refreshes the
			// associated popup and fires "annotationChanged"/"modify" so the UI re-renders.
			annotationManager.setNoteContents(annot, newLabel)
		} catch (error) {
			console.warn("Failed to update furniture annotation:", error)
		}
	}

	private findEntityAnnotation(
		entityType: "eref" | "ecall" | "furniture",
		entityId: number,
	): Core.Annotations.Annotation | undefined {
		const categories: TAnnotationCategory[] = [`${entityType}:connected`, `${entityType}:unconnected`]
		for (const category of categories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			for (const annot of set) {
				if (annot.getCustomData("entityId") === String(entityId)) {
					return annot
				}
			}
		}
		return undefined
	}

	private async centerAnnotation(
		annot: Core.Annotations.Annotation,
		anchor: "center" | "bottomLeft" = "center",
	): Promise<void> {
		if (!this.instance) return

		// Claim the latest scroll. Any earlier call's pending awaits and deferred
		// verify timers bail when they see a newer generation, so rapidly selecting
		// another annotation (or stepping a jump session) can't get yanked back.
		const myGeneration = ++this.scrollGeneration

		try {
			const { documentViewer } = this.instance.Core
			const scrollEl = documentViewer.getScrollViewElement() as HTMLElement | null
			if (!scrollEl) return

			const viewportWidthPx = scrollEl.clientWidth
			const viewportHeightPx = scrollEl.clientHeight
			const centerX = annot.X + annot.Width / 2
			const centerY = annot.Y + annot.Height / 2

			// Zoom-to-fit using the rendered (backend) rect dims so the on-screen size of
			// the highlight matches the target fill ratio regardless of page rotation.
			const rendered = this.metadataMap.get(annot.Id)?.renderedRect
			const rectWidth = rendered ? rendered.x1 - rendered.x0 : annot.Width
			const rectHeight = rendered ? rendered.y1 - rendered.y0 : annot.Height
			const targetFillRatio = 0.4
			const zoomX = rectWidth > 0 ? (viewportWidthPx * targetFillRatio) / rectWidth : 1
			const zoomY = rectHeight > 0 ? (viewportHeightPx * targetFillRatio) / rectHeight : 1
			const targetZoom = Math.max(0.5, Math.min(3, Math.min(zoomX, zoomY)))
			// zoomTo is typed `void` but returns a Promise in Apryse v11. Cast so the await
			// isn't flagged as a no-op; awaiting it lets pageToWindow read post-zoom layout.
			await (documentViewer.zoomTo(targetZoom) as unknown as Promise<void>)
			// zoomTo resolves before the viewer finishes re-laying-out the scroll content,
			// so pageToWindow / scrollWidth can still report pre-zoom values. Wait one frame
			// for the layout to settle; the timeout guarantees resolution in environments
			// where requestAnimationFrame never fires (e.g. jsdom in tests).
			await new Promise<void>((resolve) => {
				let settled = false
				const finish = () => {
					if (settled) return
					settled = true
					resolve()
				}
				if (typeof requestAnimationFrame === "function") requestAnimationFrame(finish)
				setTimeout(finish, 32)
			})
			// A newer scroll superseded this one while we awaited the zoom/settle.
			if (this.scrollGeneration !== myGeneration) return

			// pageToWindow applies the same page transform as the rendering path, so it
			// works correctly on /Rotate'd pages — unlike displayPageLocation, which
			// interprets coords in a different space and lands at the wrong spot.
			type DisplayMode = {
				pageToWindow: (pt: { x: number; y: number }, pageNumber: number) => { x: number; y: number }
			}
			const dvAny = documentViewer as unknown as {
				getDisplayModeManager?: () => { getDisplayMode?: () => DisplayMode | undefined } | undefined
				displayPageLocation: typeof documentViewer.displayPageLocation
			}
			const displayMode = dvAny.getDisplayModeManager?.()?.getDisplayMode?.()

			if (displayMode?.pageToWindow) {
				const root = scrollEl.getRootNode?.() as ShadowRoot | Document | undefined
				// Both sidebars overlay the scroll view, so the document area the user can
				// actually see is the viewport minus whatever each open panel covers. Measured
				// fresh on every settle pass (below) since the panels are resizable /
				// collapsible and may change while the post-zoom relayout settles.
				const measureInset = (selector: string, side: "left" | "right"): number => {
					const scRect = scrollEl.getBoundingClientRect?.()
					if (!scRect || !root?.querySelector) return 0
					const el = root.querySelector(selector) as HTMLElement | null
					const rect = el?.getBoundingClientRect()
					if (!rect || rect.width <= 0) return 0
					const overlap = side === "left" ? rect.right - scRect.left : scRect.right - rect.left
					return Math.max(0, Math.min(scrollEl.clientWidth, overlap))
				}

				// Clamp scroll targets to the valid range so an annot near a document edge is
				// not scrolled past the content (which would clip it). scrollWidth/Height may
				// be absent in test mocks — fall back to no upper bound there.
				const clampScroll = (value: number, max: number): number => {
					const lower = Math.max(0, value)
					return Number.isFinite(max) ? Math.min(lower, max) : lower
				}

				// Project the annotation's bounding box through pageToWindow. Take the min/max of
				// all four projected corners for a correct axis-aligned screen box at any rotation
				// — do NOT replace it with a flat `containerLeft + X*zoom` mapping, which lands
				// wrong on rotated pages.
				type TProjectedBox = { left: number; top: number; right: number; bottom: number }
				const projectCorners = (
					corners: Array<{ x: number; y: number }>,
				): TProjectedBox => {
					const pts = corners.map((pt) => displayMode.pageToWindow(pt, annot.PageNumber))
					return {
						left: Math.min(...pts.map((c) => c.x)),
						top: Math.min(...pts.map((c) => c.y)),
						right: Math.max(...pts.map((c) => c.x)),
						bottom: Math.max(...pts.map((c) => c.y)),
					}
				}
				// Project annot.X/Y — the annotation's OWN coords. applyRotation already baked the
				// page /Rotate into them (they're in the viewer's rendered space), and pageToWindow
				// maps that same space to the screen, so pageToWindow(annot) matches where the
				// highlight is actually drawn. Do NOT feed the un-rotated backend rect here:
				// pageToWindow does not re-apply /Rotate, so backend coords land at the wrong spot
				// on rotated pages (verified on a live 90° page — they pointed at the title block).
				const projectBoxViaApi = (): TProjectedBox =>
					projectCorners([
						{ x: annot.X, y: annot.Y },
						{ x: annot.X + annot.Width, y: annot.Y },
						{ x: annot.X, y: annot.Y + annot.Height },
						{ x: annot.X + annot.Width, y: annot.Y + annot.Height },
					])

				// In v11.9 the display-mode's layout model can be off from where the page is
				// ACTUALLY rendered by a constant translation (its page x-offset was ~264px
				// wrong here); pageToWindow + windowToPage stay self-consistent yet both point
				// at the wrong screen position. The error is a pure offset (zoom/rotation are
				// applied correctly), so measure the page's true origin from its container
				// element and return the delta to add on top of pageToWindow output. Works for
				// any rotation; null when the element can't be found (e.g. test mocks).
				const computeModelCorrection = (): { x: number; y: number } | null => {
					const doc = documentViewer.getDocument?.()
					const viewerEl = (
						documentViewer as unknown as { getViewerElement?: () => HTMLElement | null }
					).getViewerElement?.()
					const pageEl = (viewerEl?.querySelector?.(`#pageContainer${annot.PageNumber}`) ??
						root?.querySelector?.(`#pageContainer${annot.PageNumber}`)) as HTMLElement | null
					const pageRect = pageEl?.getBoundingClientRect()
					const scRect = scrollEl.getBoundingClientRect?.()
					const info = doc?.getPageInfo?.(annot.PageNumber)
					if (!pageRect || !scRect || pageRect.width <= 0 || !info) return null
					// The page box must be in the SAME space as the annot.X/Y coords we project —
					// the viewer's rendered space. On a 90°/270° page that space measures
					// info.height wide × info.width tall, but getPageInfo reports the dims the
					// other way round, so swap them. Using the unswapped getPageInfo dims projects
					// the wrong page corner → a wrong, over-large correction that throws the scroll
					// far off horizontally (seen live: correction.x was -2856 instead of -264).
					const rotation =
						(doc as unknown as { getPageRotation?: (p: number) => number })?.getPageRotation?.(
							annot.PageNumber,
						) ?? 0
					const swapDims = rotation === 90 || rotation === 270
					const pageW = swapDims ? info.height : info.width
					const pageH = swapDims ? info.width : info.height
					// The page's rendered (rotation-aware) bounding box, both as the model sees
					// it and as the DOM actually places it. Both are axis-aligned screen boxes
					// of the same rendered page, so their min-corners correspond.
					const modelPageBox = projectCorners([
						{ x: 0, y: 0 },
						{ x: pageW, y: 0 },
						{ x: 0, y: pageH },
						{ x: pageW, y: pageH },
					])
					const domLeft = pageRect.left - scRect.left + scrollEl.scrollLeft
					const domTop = pageRect.top - scRect.top + scrollEl.scrollTop
					return { x: domLeft - modelPageBox.left, y: domTop - modelPageBox.top }
				}

				const cornerMarginPx = 48
				// `corrected` reports whether the DOM-measured page correction was applied this
				// pass — i.e. the target page container exists. The settle loop only treats a
				// target as final once it's corrected, so it can't lock onto an uncorrected
				// position while the page is still rendering in.
				const computeTarget = (): { left: number; top: number; corrected: boolean } => {
					// Recompute everything from the LIVE layout each pass — panel insets,
					// viewport size and box position all keep changing while the viewer
					// finishes its post-zoom relayout, so values captured once go stale.
					const leftInsetPx = measureInset('[data-element="elevation-references-panel"]', "left")
					const rightInsetPx = measureInset('[data-element="reference-panel"]', "right")
					const visibleWidthPx = Math.max(1, scrollEl.clientWidth - leftInsetPx - rightInsetPx)
					// pageToWindow handles rotation; the DOM-measured correction fixes the
					// model's constant offset error. Skip the correction when unavailable.
					const box = projectBoxViaApi()
					const correction = computeModelCorrection()
					if (correction) {
						box.left += correction.x
						box.right += correction.x
						box.top += correction.y
						box.bottom += correction.y
					}
					const corrected = correction !== null
					const liveMaxLeft = Number.isFinite(scrollEl.scrollWidth)
						? Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth)
						: Number.POSITIVE_INFINITY
					const liveMaxTop = Number.isFinite(scrollEl.scrollHeight)
						? Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight)
						: Number.POSITIVE_INFINITY
					if (anchor === "bottomLeft") {
						// Anchor the annotation's bottom-left near the visible region's
						// bottom-left, anchoring the box EDGES, not the center — anchoring the
						// center pushes a wide highlight half off-screen.
						return {
							left: clampScroll(box.left - leftInsetPx - cornerMarginPx, liveMaxLeft),
							top: clampScroll(box.bottom - scrollEl.clientHeight + cornerMarginPx, liveMaxTop),
							corrected,
						}
					}
					// Center the annotation within the visible region (between the panels).
					return {
						left: clampScroll(
							(box.left + box.right) / 2 - leftInsetPx - visibleWidthPx / 2,
							liveMaxLeft,
						),
						top: clampScroll((box.top + box.bottom) / 2 - scrollEl.clientHeight / 2, liveMaxTop),
						corrected,
					}
				}
				// Apply, then re-measure and re-apply until the target stops moving. On the FIRST
				// selection the target page is usually not rendered yet (Apryse virtualizes far
				// pages, and the eref click also opens the right panel / resizes the viewport), so
				// the page container is absent and the model-offset correction can't run — the
				// scroll lands ~one offset short ("half hidden"). The first pass brings the page
				// into view; a later pass measures it and corrects, so a single click lands right
				// instead of needing a second. Assign scrollLeft/scrollTop directly — scrollTo({})
				// is not reliably honored by the viewer's scroll element.
				let target = { left: 0, top: 0, corrected: false }
				let prevTarget: { left: number; top: number } | null = null
				for (let pass = 0; pass < 6; pass++) {
					// A newer selection superseded this one — stop so we don't fight it.
					if (this.scrollGeneration !== myGeneration) return
					target = computeTarget()
					scrollEl.scrollLeft = target.left
					scrollEl.scrollTop = target.top
					// Done once the correction has been applied (page container exists) AND two
					// passes in a row agree (page rendered, panels stable). Requiring `corrected`
					// prevents locking onto an uncorrected target while the page is still paging in.
					if (
						target.corrected &&
						prevTarget &&
						Math.abs(target.left - prevTarget.left) <= 1 &&
						Math.abs(target.top - prevTarget.top) <= 1
					) {
						break
					}
					prevTarget = target
					await new Promise<void>((resolve) => {
						let settled = false
						const finish = () => {
							if (settled) return
							settled = true
							resolve()
						}
						if (typeof requestAnimationFrame === "function") requestAnimationFrame(finish)
						setTimeout(finish, 32)
					})
				}

				return
			}

			// Test-mock fallback: real Apryse always exposes pageToWindow.
			dvAny.displayPageLocation(annot.PageNumber, centerX, centerY, false)
		} catch (error) {
			console.warn("Failed to center annotation:", error)
		}
	}

	// Map a screen point to a point in the backend's furniture-coordinate space — the same
	// space `TFurniture.coordinates` and the duplicate endpoint use. Returns the 0-based
	// `pageNumber` plus `x`/`y`, or null when the point isn't over a rendered page. Accepts
	// client coords so it serves both right-click events and the keyboard-paste path.
	//
	// Crucially, backend furniture coords are in the page's RENDERED (rotation-applied)
	// orientation — the same orientation as the `#pageContainer{n}` DOM box and `getPageInfo`
	// (`applyRotation` converts these into Apryse's un-rotated annotation space only for
	// drawing). So this mapping is a direct fractional scale of the DOM rect into page units;
	// it must NOT re-apply / invert the page /Rotate (verified on a live 90° page: doing so
	// landed the paste far from the cursor; the direct scale matches exactly).
	//
	// We measure the real DOM rect (not windowToPage / getZoomLevel) to sidestep the known
	// layout-model offset bug.
	getPagePointFromClient(clientX: number, clientY: number): { pageNumber: number; x: number; y: number } | null {
		if (!this.instance) return null
		const { documentViewer } = this.instance.Core
		const scrollEl = documentViewer.getScrollViewElement() as HTMLElement | null
		const viewerEl = (
			documentViewer as unknown as { getViewerElement?: () => HTMLElement | null }
		).getViewerElement?.()
		const root = scrollEl?.getRootNode?.() as ShadowRoot | Document | undefined
		const searchRoot: ParentNode | null = viewerEl ?? root ?? null
		if (!searchRoot?.querySelectorAll) return null

		// Find the page container the cursor is inside. Ids are `pageContainer{viewerPageNumber}`.
		let hitPage: number | null = null
		let hitRect: DOMRect | null = null
		const containers = searchRoot.querySelectorAll<HTMLElement>('[id^="pageContainer"]')
		for (const el of Array.from(containers)) {
			const match = /^pageContainer(\d+)$/.exec(el.id)
			if (!match) continue
			const rect = el.getBoundingClientRect()
			if (rect.width <= 0 || rect.height <= 0) continue
			if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
				hitPage = Number(match[1])
				hitRect = rect
				break
			}
		}
		if (hitPage == null || !hitRect) return null

		const doc = documentViewer.getDocument?.()
		if (!doc) return null
		const info = doc.getPageInfo?.(hitPage)
		if (!info || info.width <= 0 || info.height <= 0) return null

		// getPageInfo is rotation-aware (rendered dims) and matches the DOM rect orientation, so
		// the cursor fraction maps straight into backend page units — no rotation/swap.
		const x = ((clientX - hitRect.left) / hitRect.width) * info.width
		const y = ((clientY - hitRect.top) / hitRect.height) * info.height

		return { pageNumber: hitPage - 1, x, y }
	}

	// Rendered (rotation-aware) dimensions of a page, in the backend furniture-coordinate
	// space — the same space `TFurniture.coordinates` and the duplicate endpoint use. Accepts
	// the backend's 0-based `pageNumber`; returns null when the page can't be measured. Used by
	// the Duplicate flow to know the right page edge for collision avoidance.
	getPageDimensions(pageNumber: number): { width: number; height: number } | null {
		if (!this.instance) return null
		const doc = this.instance.Core.documentViewer.getDocument?.()
		if (!doc) return null
		const info = doc.getPageInfo?.(pageNumber + 1)
		if (!info || info.width <= 0 || info.height <= 0) return null
		return { width: info.width, height: info.height }
	}

	// Draw a single furniture highlight without re-rendering the whole annotation layer
	// (renderAnnotations clears+redraws everything). Mirrors Pass 3 of renderAnnotations for
	// one item — used after a paste/duplicate so the new object appears and can be selected.
	addFurnitureAnnotation(furniture: TFurniture): void {
		if (!this.instance) return
		// Don't double-add if it's already tracked.
		if (this.findEntityAnnotation("furniture", furniture.id)) return

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = furniture.page_number + 1
		const transform = this.buildPageRotationMap(doc, [
			{ page_number: furniture.page_number } as TPageElevationData,
		]).get(viewerPageNumber)

		const status: TAnnotationStatus = "connected"
		const category: TAnnotationCategory = `furniture:${status}`
		const fillState = resolveEntityFillState(furniture.status, status)
		const coords = this.applyRotation(furniture.coordinates, transform)

		const annot = new Annotations.RectangleAnnotation({
			PageNumber: viewerPageNumber,
			X: coords.x0,
			Y: coords.y0,
			Width: coords.x1 - coords.x0,
			Height: coords.y1 - coords.y0,
		})
		this.applyStyle(annot, fillState, Annotations)
		this.setReadOnly(annot)
		annot.setContents(this.buildFurnitureLabel(furniture))

		annot.setCustomData("entityId", String(furniture.id))
		annot.setCustomData("entityType", "furniture")
		annot.setCustomData("status", status)
		annot.setCustomData("entityStatus", furniture.status ?? "")
		annot.setCustomData("erefId", String(furniture.eref_id))
		annot.setCustomData("category", furniture.category?.name ?? "")
		annot.setCustomData("pageNumber", String(furniture.page_number))

		const metadata: TAnnotationMetadata = {
			entityId: furniture.id,
			entityType: "furniture",
			status,
			pageNumber: furniture.page_number,
			erefId: furniture.eref_id ?? undefined,
			renderedRect: furniture.coordinates,
		}
		this.trackAnnotation(annot, category, metadata)

		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
	}

	cleanup(): void {
		if (this.instance && this.annotationSelectedHandler) {
			this.instance.Core.annotationManager.removeEventListener("annotationSelected", this.annotationSelectedHandler)
		}
		this.annotationSelectedHandler = null
		this.clearAllAnnotations()
		this.instance = null
		this.onAnnotationSelected = undefined
	}

	private buildErefLabel(elevationNumber: string | null | undefined): string {
		return elevationNumber ? `Elevation ${elevationNumber}` : "Elevation"
	}

	private buildEcallLabel(ecall: TEcall): string {
		const linked = ecall.elevation_numbers.map((en) => en.elevation_number).filter(Boolean)
		if (linked.length > 0) return `→ ${linked.join(", ")}`
		return ecall.room_name ? `Callout · ${ecall.room_name}` : "Callout"
	}

	private buildFurnitureLabel(furniture: TFurniture): string {
		const modelLabel = furniture.category?.model_label
		const modelNumber = furniture.selected_model_number?.model_number
		const parts = [modelLabel, modelNumber].filter(Boolean)
		if (parts.length > 0) return parts.join(" · ")
		return furniture.category?.name ?? "Furniture"
	}

	// Build a per-viewer-page-number map describing how to rotate backend coords so
	// they line up with the rendered page. Backend coords come in original (un-rotated)
	// PDF page space; WebViewer renders /Rotate'd pages so we need to pre-rotate the
	// rect coordinates by the same angle to make them align.
	private buildPageRotationMap(
		doc: ReturnType<Core.DocumentViewer["getDocument"]> | null,
		pages: TPageElevationData[],
	): Map<number, TPageRotationTransform> {
		const result = new Map<number, TPageRotationTransform>()
		if (!doc || typeof doc.getPageRotation !== "function" || typeof doc.getPageInfo !== "function") {
			return result
		}

		const seen = new Set<number>()
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			if (seen.has(viewerPageNumber)) continue
			seen.add(viewerPageNumber)

			let rotation = 0
			let info: { width: number; height: number } | undefined
			try {
				rotation = doc.getPageRotation(viewerPageNumber) ?? 0
				info = doc.getPageInfo(viewerPageNumber)
			} catch (error) {
				console.warn(`[Annotations] Could not read rotation for page ${viewerPageNumber}:`, error)
				continue
			}

			// getPageInfo returns the rendered (rotation-aware) page dims, and the
			// backend supplies coordinates in those same dims, so we use them directly
			// — no swap.
			const origWidth = info?.width ?? 0
			const origHeight = info?.height ?? 0

			if (rotation !== 0) {
				console.log(`[Annotations] Page ${viewerPageNumber} is rotated ${rotation}°`)
			}

			result.set(viewerPageNumber, { rotation, origWidth, origHeight })
		}
		return result
	}

	private applyRotation(
		coords: { x0: number; y0: number; x1: number; y1: number },
		transform: TPageRotationTransform | undefined,
	): { x0: number; y0: number; x1: number; y1: number } {
		if (!transform || transform.rotation === 0) return coords
		// Backend supplies coords in the un-rotated PDF page space. To align with the
		// rendered (rotated) viewer space we apply the CCW inverse of the page's
		// /Rotate. w/h are the un-rotated PDF page dims.
		const { rotation, origWidth: w, origHeight: h } = transform
		const { x0, y0, x1, y1 } = coords
		switch (rotation) {
			case 90:
				return { x0: y0, y0: w - x1, x1: y1, y1: w - x0 }
			case 180:
				return { x0: w - x1, y0: h - y1, x1: w - x0, y1: h - y0 }
			case 270:
				return { x0: h - y1, y0: x0, x1: h - y0, y1: x1 }
			default:
				return coords
		}
	}

	private applyStyle(
		annot: Core.Annotations.RectangleAnnotation,
		fillState: TAnnotationFillState,
		Annotations: typeof Core.Annotations,
	): void {
		const style = ANNOTATION_STYLES[fillState]
		const [fr, fg, fb, fa] = style.fillColor

		annot.StrokeThickness = 0
		annot.FillColor = new Annotations.Color(fr, fg, fb, fa)
	}

	private setReadOnly(annot: Core.Annotations.RectangleAnnotation): void {
		annot.NoMove = true
		annot.NoDelete = true
		annot.NoRotate = true
	}

	private trackAnnotation(
		annot: Core.Annotations.Annotation,
		category: TAnnotationCategory,
		metadata: TAnnotationMetadata,
	): void {
		if (!this.annotationsByCategory.has(category)) {
			this.annotationsByCategory.set(category, new Set())
		}
		this.annotationsByCategory.get(category)!.add(annot)
		this.metadataMap.set(annot.Id, metadata)
	}

	private setupClickListener(): void {
		if (!this.instance) return

		const { annotationManager } = this.instance.Core
		// Remove any previously-registered handler first so repeated setInstance calls on the
		// same shared instance don't stack duplicate listeners (#1119).
		if (this.annotationSelectedHandler) {
			annotationManager.removeEventListener("annotationSelected", this.annotationSelectedHandler)
		}
		const handler = (annotations: Core.Annotations.Annotation[], action: string) => {
			if (action === "selected" && annotations.length > 0) {
				const metadata = this.metadataMap.get(annotations[0].Id)
				if (metadata) {
					this.onAnnotationSelected?.(metadata)
				}
			}
		}
		this.annotationSelectedHandler = handler
		annotationManager.addEventListener("annotationSelected", handler)
	}
}

export default new AnnotationService()
